import { Fragment, type ReactNode } from "react";
import classNames from "classnames";
import { TagOverflow } from "@design-system/Tag/TagOverflow";
import { VStack } from "@design-system/VStack/VStack";
import type { BoardCardField, BoardCardFieldPlacement } from "./Board.types";
import {
    BoardCardFooter,
    BoardCardHeader,
    BoardCardMeta,
    BoardCardTitle,
} from "./BoardCardSlots";
import { getNodeText } from "./boardCardText";
import { BOARD_TAB_STOP_ATTRIBUTE } from "./boardSensors";
import styles from "./BoardCardLayout.module.scss";

interface BoardCardLayoutProps {
    title: ReactNode;
    /** Before the title and centred on its line, such as a 16px avatar. */
    leading?: ReactNode;
    /** Fixed badge at the end of the title row, such as a status pill. */
    badge?: ReactNode;
    /**
     * A `BoardCardMenu` at the very end of the title row, in a 20px slot it
     * always takes. Shown while the card is hovered or holds focus, and while
     * the menu is open.
     */
    menu?: ReactNode;
    fields: readonly BoardCardField[];
}

type FieldRow =
    | { key: string; kind: "stacked"; field: BoardCardField }
    | {
          key: "inline";
          kind: "inline";
          fields: readonly BoardCardField[];
          endFields: readonly BoardCardField[];
      }
    | { key: "bottomEnd"; kind: "empty" };

const MENU_SLOT_PROPS = { [BOARD_TAB_STOP_ATTRIBUTE]: "" };

/** Whether a card field's content renders: `BoardCardLayout` drops a field without it. */
export const hasFieldContent = (content: ReactNode) =>
    content !== null && content !== undefined && content !== false;

const isFieldShown = ({ content }: BoardCardField) => hasFieldContent(content);

/** Tags drawn before the tag row's `+N`. */
const MAX_TAGS = 3;

/**
 * A tag in a shrinkable slot. The last one drawn shrinks first
 * (`data-shrinks-first`), so the earlier ones keep their text longest.
 */
const toTagOverflowItem = (
    { id, content, label }: BoardCardField,
    index: number,
    fields: readonly BoardCardField[],
) => ({
    id,
    content: (
        <span
            className={styles.tag}
            data-shrinks-first={
                index === Math.min(fields.length, MAX_TAGS) - 1 || undefined
            }
        >
            {content}
        </span>
    ),
    label: label ?? (getNodeText(content) || id),
});

const FieldGroup = ({
    fields,
    className,
}: Readonly<{ fields: readonly BoardCardField[]; className?: string }>) => (
    <BoardCardMeta className={classNames(styles.group, className)}>
        {fields.map(({ id, content }) => (
            <Fragment key={id}>{content}</Fragment>
        ))}
    </BoardCardMeta>
);

const getPlacedFields = (
    fields: readonly BoardCardField[],
    placement: BoardCardFieldPlacement,
) => fields.filter((field) => field.placement === placement);

const getRows = (shownFields: readonly BoardCardField[]): FieldRow[] => {
    const stackedRows = getPlacedFields(shownFields, "stacked").map(
        (field): FieldRow => ({
            key: `stacked:${field.id}`,
            kind: "stacked",
            field,
        }),
    );
    const inlineFields = getPlacedFields(shownFields, "inline");
    const endFields = getPlacedFields(shownFields, "inlineEnd");
    return inlineFields.length === 0 && endFields.length === 0
        ? stackedRows
        : [
              ...stackedRows,
              {
                  key: "inline",
                  kind: "inline",
                  fields: inlineFields,
                  endFields,
              },
          ];
};

const RowContent = ({ row }: Readonly<{ row: FieldRow }>) => {
    if (row.kind === "stacked") {
        return <FieldGroup fields={[row.field]} />;
    }
    if (row.kind === "empty") {
        return null;
    }
    return (
        <span className={styles.tagRow}>
            {row.fields.length > 0 && (
                <TagOverflow
                    items={row.fields.map(toTagOverflowItem)}
                    max={MAX_TAGS}
                    triggerTabIndex={-1}
                    triggerTooltip="Other fields"
                    className={styles.tags}
                />
            )}
            {row.endFields.length > 0 && (
                <span className={styles.endFields}>
                    {row.endFields.map(({ id, content }) => (
                        <Fragment key={id}>{content}</Fragment>
                    ))}
                </span>
            )}
        </span>
    );
};

/**
 * Card content for `BoardCard` that places each field where it declares:
 * the title row (`leading`, the one-line title, `topEnd` fields, the badge,
 * then the menu), one context row per `stacked` field, then one tag row of the
 * `inline` fields, the first three shown and the rest behind a `+N` tag,
 * followed by every `inlineEnd` field (trailing controls, never behind the
 * `+N`). The tag row is one line that never wraps: short on space, the shown
 * tags cut their text with an ellipsis, the last first, while the `+N`, the
 * `inlineEnd` fields and the `bottomEnd` fields keep their size and place, so
 * a control that only shows on hover must keep its space (fade it in with
 * opacity) to keep the card still. `bottomEnd` fields sit right-aligned on
 * the last of those rows: the tag row, else the last stacked row, else a row
 * of their own under the title (never the title row). Fields keep their declared order within a
 * placement; hidden fields leave no empty row. For other layouts, compose
 * `BoardCardHeader`, `BoardCardMeta` and `BoardCardFooter` directly.
 */
export const BoardCardLayout = ({
    title,
    leading,
    badge,
    menu,
    fields,
}: Readonly<BoardCardLayoutProps>) => {
    const shownFields = fields.filter(isFieldShown);
    const topEndFields = getPlacedFields(shownFields, "topEnd");
    const bottomEndFields = getPlacedFields(shownFields, "bottomEnd");
    const rows = getRows(shownFields);
    if (rows.length === 0 && bottomEndFields.length > 0) {
        rows.push({ key: "bottomEnd", kind: "empty" });
    }
    const lastRowKey = rows.at(-1)?.key;

    return (
        <>
            <BoardCardHeader>
                {hasFieldContent(leading) && (
                    <span className={styles.leading}>{leading}</span>
                )}
                <BoardCardTitle>{title}</BoardCardTitle>
                {topEndFields.length > 0 && (
                    <FieldGroup
                        fields={topEndFields}
                        className={styles.topEnd}
                    />
                )}
                {badge}
                {hasFieldContent(menu) && (
                    <span className={styles.menu} {...MENU_SLOT_PROPS}>
                        {menu}
                    </span>
                )}
            </BoardCardHeader>
            {rows.length > 0 && (
                <VStack gap={10}>
                    {rows.map((row) =>
                        row.key === lastRowKey && bottomEndFields.length > 0 ? (
                            <BoardCardFooter key={row.key}>
                                <RowContent row={row} />
                                <FieldGroup
                                    fields={bottomEndFields}
                                    className={styles.bottomEnd}
                                />
                            </BoardCardFooter>
                        ) : (
                            <RowContent key={row.key} row={row} />
                        ),
                    )}
                </VStack>
            )}
        </>
    );
};
